import React, { useEffect, useState } from "react";
import { Tag, Plus, Trash2, Edit3, Check, X } from "lucide-react";
import { listFlagDefinitions, createFlagDefinition, updateFlagDefinition, deleteFlagDefinition } from "../../services/catalogService";

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#6366f1", "#a855f7", "#6b7280"];

const FlagDefinitionsManager = () => {
    const [definitions, setDefinitions] = useState([]);
    const [newName, setNewName] = useState("");
    const [newColor, setNewColor] = useState(COLORS[0]);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [loading, setLoading] = useState(true);

    const load = () => listFlagDefinitions().then((d) => setDefinitions(d.definitions || [])).finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        await createFlagDefinition(newName.trim(), newColor);
        setNewName("");
        load();
    };

    const handleUpdate = async (id) => {
        if (!editingName.trim()) return;
        await updateFlagDefinition(id, { name: editingName.trim() });
        setEditingId(null);
        load();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this flag? This removes it from all products.")) return;
        await deleteFlagDefinition(id);
        load();
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg p-5">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><Tag size={18} className="text-teal-600" /> Flags</h3>
            <p className="text-xs text-gray-400 mb-4">
                Tags you can attach to any product — Damaged, Gated, Hazmat, Priority — then filter your catalog by them.
            </p>

            <div className="flex gap-2 mb-3 flex-wrap">
                {COLORS.map((c) => (
                    <button key={c} onClick={() => setNewColor(c)} style={{ backgroundColor: c }}
                        className={`w-6 h-6 rounded-full border-2 ${newColor === c ? "border-gray-800" : "border-transparent"}`} />
                ))}
            </div>
            <div className="flex gap-2 mb-4">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    placeholder="e.g. Damaged" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                <button onClick={handleCreate} className="px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg text-sm font-bold hover:opacity-90 transition flex items-center gap-1">
                    <Plus size={15} /> Add
                </button>
            </div>

            {loading ? <p className="text-sm text-gray-400">Loading...</p> : (
                <div className="space-y-2">
                    {definitions.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 border border-gray-100 rounded-lg px-3 py-2">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                            {editingId === d.id ? (
                                <>
                                    <input value={editingName} onChange={(e) => setEditingName(e.target.value)}
                                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-sm" autoFocus />
                                    <button onClick={() => handleUpdate(d.id)} className="text-green-600"><Check size={16} /></button>
                                    <button onClick={() => setEditingId(null)} className="text-gray-400"><X size={16} /></button>
                                </>
                            ) : (
                                <>
                                    <span className="flex-1 text-sm font-medium text-gray-700">{d.name}</span>
                                    <button onClick={() => { setEditingId(d.id); setEditingName(d.name); }} className="text-gray-400 hover:text-teal-600"><Edit3 size={14} /></button>
                                    <button onClick={() => handleDelete(d.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                                </>
                            )}
                        </div>
                    ))}
                    {definitions.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No flags yet.</p>}
                </div>
            )}
        </div>
    );
};

export default FlagDefinitionsManager;